import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * /grades/:gradeId — grade page. Tabs:
 *   - "מסלולים": table of tracks (drag-to-reorder, row click → track page).
 *   - "מסלול מומלץ": the cross-track recommended-path editor.
 * The tab lives in the URL (?tab=recommended) so refresh restores state and
 * the sidebar/breadcrumb match.
 *
 * Unknown/deleted grade id → 404 card with a "חזור לכיתות" link.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { Alert, Button, Card, Spin, Tabs, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { deleteTrack, listGrades, listTracks, reorderTracks, } from '../../api';
import { PageHeader } from '../../components/PageHeader';
import { notifySuccess } from '../../components/errors';
import { useLoader } from '../../components/useLoader';
import { SortableTable } from '../../components/SortableTable';
import { actionsCol, emptyState, navRowProps } from '../../components/tableHelpers';
import { TrackModal } from '../../components/content/TrackModal';
import { RecommendedPathEditor } from '../../components/content/RecommendedPathEditor';
export default function GradePageScreen() {
    const nav = useNavigate();
    const { gradeId: gradeIdParam } = useParams();
    const gradeId = Number(gradeIdParam);
    const [search, setSearch] = useSearchParams();
    const tab = search.get('tab') === 'recommended' ? 'recommended' : 'tracks';
    const [grade, setGrade] = useState(undefined); // undefined = loading
    // Grade info comes from the list endpoint — cheaper than a per-grade GET
    // and it's the same data shape (no /admin/grades/:id GET exists).
    useEffect(() => {
        (async () => {
            try {
                const all = await listGrades();
                setGrade(all.find((g) => g.id === gradeId) ?? null);
            }
            catch {
                setGrade(null); /* client auto-surfaces */
            }
        })();
    }, [gradeId]);
    if (grade === undefined)
        return _jsx(Spin, {});
    if (grade === null) {
        return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: "\u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0", breadcrumb: [{ label: 'ניהול' }, { label: 'כיתות', to: '/grades' }, 'לא נמצא'] }), _jsx(Alert, { type: "warning", message: "\u05D4\u05DB\u05D9\u05EA\u05D4 \u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0\u05D4", description: _jsx(_Fragment, { children: _jsx(Link, { to: "/grades", children: "\u05D7\u05D6\u05D5\u05E8 \u05DC\u05E8\u05E9\u05D9\u05DE\u05EA \u05D4\u05DB\u05D9\u05EA\u05D5\u05EA" }) }) })] }));
    }
    const setTab = (key) => {
        if (key === 'tracks')
            setSearch({});
        else
            setSearch({ tab: key });
    };
    return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: grade.name, breadcrumb: [
                    { label: 'ניהול' },
                    { label: 'כיתות', to: '/grades' },
                    grade.name,
                ] }), _jsx(Tabs, { activeKey: tab, onChange: setTab, items: [
                    { key: 'tracks', label: 'מסלולים', children: _jsx(TracksPanel, { grade: grade, nav: nav }) },
                    { key: 'recommended', label: 'מסלול הלימוד המומלץ', children: _jsx(RecommendedPathEditor, { gradeId: grade.id }) },
                ] })] }));
}
function TracksPanel({ grade, nav }) {
    const { data, loading, reload } = useLoader(() => listTracks(grade.id), [grade.id]);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null);
    const del = async (t) => {
        try {
            await deleteTrack(t.id);
            notifySuccess('המסלול נמחק');
            reload();
        }
        catch { /* client auto-surfaces */ }
    };
    const cols = [
        { title: 'שם', dataIndex: 'name' },
        { title: 'צבע', dataIndex: 'color', width: 90, render: (c) => c ? _jsx(Tag, { color: c, children: c }) : '—' },
        actionsCol((t) => setEditing(t), del),
    ];
    return (_jsxs(Card, { extra: _jsx(Button, { size: "small", icon: _jsx(PlusOutlined, {}), onClick: () => setCreating(true), children: "\u05DE\u05E1\u05DC\u05D5\u05DC \u05D7\u05D3\u05E9" }), title: `${data?.length ?? 0} מסלולים`, children: [(!loading && (data?.length ?? 0) === 0)
                ? emptyState('אין עדיין מסלולים — צור את הראשון', { label: 'מסלול חדש', onClick: () => setCreating(true) })
                : (_jsx(SortableTable, { size: "small", loading: loading, dataSource: data ?? [], columns: cols, onRow: navRowProps((t) => nav(`/grades/${grade.id}/tracks/${t.id}`)), onReorder: async (ids) => { await reorderTracks(grade.id, ids); reload(); } })), _jsx(TrackModal, { open: creating, onClose: () => setCreating(false), onDone: () => { setCreating(false); reload(); }, gradeId: grade.id }), editing && (_jsx(TrackModal, { open: true, onClose: () => setEditing(null), onDone: () => { setEditing(null); reload(); }, gradeId: grade.id, initial: editing }))] }));
}
